import React, { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import PopupModal from '../components/PopupModal';

const contactInfo = [
  {
    icon: <Phone className="w-5 h-5 text-blue-900" />,
    label: "Call Us",
    value: "+91 92666 04406",
    href: "tel:+919266604406",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-green-500">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
    label: "WhatsApp",
    value: "+91 92666 04406",
    href: "https://wa.me/919266604406?text=Hi%2C%20I%20want%20to%20order%20a%20SIM%20card%20from%20A1%20Sims",
  },
  {
    icon: <Mail className="w-5 h-5 text-orange-500" />,
    label: "Email",
    value: "support@a1sims.com",
    href: "mailto:support@a1sims.com",
  },
  {
    icon: <MapPin className="w-5 h-5 text-rose-600" />,
    label: "Service Areas",
    value: "Delhi, Noida, Ghaziabad & Greater Noida",
    href: null,
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ fullName: "", email: "", subject: "", message: "" });
  const [popup, setPopup] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPopup(true);
    setForm({ fullName: "", email: "", subject: "", message: "" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-slate-50 border-b border-slate-200 py-16 text-center px-4">
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-600 text-xs font-bold mb-4">
            <Mail className="w-3.5 h-3.5" />
            Get In Touch
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight mb-3">
            Contact Us
          </h1>
          <p className="text-slate-500 text-base leading-relaxed">
            Have a question about your SIM order? Need help with your plan? Our team is here to help you every step of the way.
          </p>
        </div>
      </section>

      {/* Main Grid */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 items-start">
        {/* Left: Contact Info */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Reach Out To Us</h2>
          <p className="text-slate-500 mb-8 text-sm">
            We're available Monday to Saturday, 10 AM – 8 PM. Typically respond within 2 hours.
          </p>

          <div className="flex flex-col gap-4">
            {contactInfo.map((item) => (
              <div key={item.label} className="flex items-start gap-4 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-bold text-slate-900 hover:text-orange-600 transition-colors">
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-sm font-bold text-slate-900">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-2xl bg-orange-50/70 border border-orange-200">
            <p className="text-sm font-bold text-orange-700 mb-1">⚡ Quick SIM Delivery</p>
            <p className="text-xs text-orange-900/80">We deliver and activate your SIM within 2 hours across Delhi, Noida, Ghaziabad & Greater Noida.</p>
          </div>
        </div>

        {/* Right: Message Form */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-md p-8">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Send Us a Message</h2>
          <p className="text-xs text-slate-500 mb-6">Fill out the form and we'll get back to you shortly.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Full Name *</label>
              <input
                type="text"
                required
                placeholder="Your full name"
                value={form.fullName}
                onChange={e => setForm({ ...form, fullName: e.target.value })}
                className="w-full p-3 rounded-xl border border-slate-200 text-xs outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Email Address *</label>
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full p-3 rounded-xl border border-slate-200 text-xs outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Subject *</label>
              <input
                type="text"
                required
                placeholder="How can we help?"
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                className="w-full p-3 rounded-xl border border-slate-200 text-xs outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Message *</label>
              <textarea
                rows={4}
                required
                placeholder="Tell us more about your query..."
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                className="w-full p-3 rounded-xl border border-slate-200 text-xs outline-none focus:ring-2 focus:ring-orange-500"
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-xl text-xs shadow-md transition"
            >
              Send Message
            </button>
          </form>
        </div>
      </section>

      <PopupModal
        isOpen={popup}
        title="Message Sent!"
        message="Thank you for contacting us! We will get back to you shortly."
        onClose={() => setPopup(false)}
      />
    </div>
  );
}